import {useContext, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import PrimitiveTypography, {primitiveTypographyDesign} from '@s-ui/react-primitive-typography'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import {useNetLoading} from '@adv-mt/mt-net-drawer'
import {PanelBasic} from '@adv-mt/mt-panel-basic'
import {AiIcon} from '@adv-mt/ui/ai-icon'
import {Button, BUTTON_INTENTS, BUTTON_SHAPES, BUTTON_SIZES, BUTTON_VARIANTS} from '@adv-mt/ui/button'
import {ICON_SIZES} from '@adv-mt/ui/icon'

const BASE_CLASS = 'mt-NetSuggestions'

const HOME_SUGGESTIONS = [
  {
    text: 'Deportivo o clásico potente'
  },
  {
    text: 'Urbano fiable que consuma poco'
  },
  {
    text: 'Familiar o SUV con maletero grande'
  },
  {
    text: 'Furgoneta para trabajar'
  }
]

const NetSuggestions = ({
  onClickOption,
  page,
  adId,
  showHeader = false,
  isPanel = false,
  isCentered = false,
  isOpenNetButtonVisible = true,
  isInlineOpenNetButton = false,
  customHeader,
  className = ''
}) => {
  const {i18n, domain} = useContext(Context)
  const {isLoading: isNetLoading} = useNetLoading()

  const config = domain.get('config')
  const {NET_SUGGESTION_TYPE, MESSAGE_TYPES} = config.get('COMMON').AI_ASSISTANT

  const isMobile = config.get('isMobile')
  const isHigherThanTabletResolution = useMediaQuery(useMediaQueryBreakpoints.lg, {
    defaultMatches: !isMobile
  })

  const pageType = page || NET_SUGGESTION_TYPE?.AD_LISTING
  const isHomePage = pageType === NET_SUGGESTION_TYPE?.HOME

  const [suggestions, setSuggestions] = useState(() => (isHomePage ? HOME_SUGGESTIONS : []))

  useMount(() => {
    if (!isHomePage) getSuggestions()
  })

  const getSuggestions = async () => {
    const [error, response] = await domain
      .get('get_assistant_chat_suggestions_use_case')
      .execute({type: pageType, adId})

    if (!error) {
      setSuggestions(response?.suggestions || [])
    }
  }

  const onClickSuggestion = text => {
    onClickOption({text, messageType: MESSAGE_TYPES?.PREDEFINED, suggestions})
  }

  const onClickOpenNet = () => {
    onClickOption({suggestions})
  }

  const ContainerWrapper = isPanel ? PanelBasic : 'div'

  const openNetButtonProps = {
    onClick: onClickOpenNet,
    size: BUTTON_SIZES.MEDIUM,
    variant: BUTTON_VARIANTS.LINK
  }
  const inlineButtonProps = {
    size: BUTTON_SIZES.MEDIUM,
    variant: BUTTON_VARIANTS.OUTLINE,
    shape: BUTTON_SHAPES.CIRCLE
  }
  const suggestionButtonProps = {
    ...inlineButtonProps,
    disabled: isNetLoading
  }

  if (!suggestions || suggestions?.length === 0) return null

  return (
    <ContainerWrapper>
      <div className={`${BASE_CLASS} ${className}`}>
        {customHeader && <div className={`${BASE_CLASS}-header`}>{customHeader(isNetLoading)}</div>}

        {showHeader && !customHeader && (
          <div className={`${BASE_CLASS}-header`}>
            <AiIcon loading={isNetLoading} size={ICON_SIZES.MD} />
            <PrimitiveTypography design={primitiveTypographyDesign.HEADLINE_2} className={`${BASE_CLASS}-headerTitle`}>
              {i18n.t('NET.ACCESS.SUGGESTIONS.TITLE')}
            </PrimitiveTypography>

            {isOpenNetButtonVisible && !isInlineOpenNetButton && isHigherThanTabletResolution && (
              <Button {...openNetButtonProps} className={`${BASE_CLASS}-headerButton`}>
                {i18n.t('NET.ACCESS.SUGGESTIONS.SEE_NET_CTA')}
              </Button>
            )}
          </div>
        )}

        <div className={cx(`${BASE_CLASS}-suggestions`, {[`${BASE_CLASS}-suggestions--centered`]: isCentered})}>
          {suggestions.map(({text}, index) => (
            <Button key={`${index}-${text}`} {...suggestionButtonProps} onClick={() => onClickSuggestion(text)}>
              {text}
            </Button>
          ))}

          {isOpenNetButtonVisible && isInlineOpenNetButton && (
            <Button {...inlineButtonProps} intent={BUTTON_INTENTS.AI} onClick={onClickOpenNet}>
              {i18n.t('NET.ACCESS.SUGGESTIONS.ASK_ANYTHING')}
            </Button>
          )}
        </div>

        {isOpenNetButtonVisible && !isInlineOpenNetButton && !isHigherThanTabletResolution && (
          <Button {...openNetButtonProps} className={`${BASE_CLASS}-headerButton`}>
            {i18n.t('NET.ACCESS.SUGGESTIONS.SEE_NET_CTA')}
          </Button>
        )}
      </div>
    </ContainerWrapper>
  )
}

NetSuggestions.displayName = 'NetSuggestions'
NetSuggestions.propTypes = {
  // center the suggestions row
  isCentered: PropTypes.bool,
  // method to open net
  onClickOption: PropTypes.func,
  // page type for assistant context
  page: PropTypes.string,
  // ad id related to page type for assistant context
  adId: PropTypes.string,
  // show header with title + see net button
  showHeader: PropTypes.bool,
  // show content inside a panel component
  isPanel: PropTypes.bool,
  // show the "Consultar a Net" button
  isOpenNetButtonVisible: PropTypes.bool,
  // render prop for the open net button, placed as the last suggestion chip: ({onClick, disabled}) => node
  isInlineOpenNetButton: PropTypes.bool,
  // render prop replacing the default header, receives the AI loading state: isNetLoading => node
  customHeader: PropTypes.func,
  // additional CSS classes for the component
  className: PropTypes.string
}

export default NetSuggestions
